/**
 * "Prior loans & servicing" — one person's other EV / LQ loans with live FCI
 * status (frontend_documentation/applicant-history.md).
 *
 * Layout, top to bottom: header (title · Checked {ago} · refresh), the
 * partial-results banner when FCI / Salesforce couldn't be searched, one
 * verdict banner for the person, a card per prior loan (collapsed: address,
 * LOS / ref / role / product / funded, the FCI pill and balance; expanded: the
 * FCI note, term progress, the servicing fields and how the match was made),
 * name-only "possible" matches under their own greyed heading, then the
 * identifiers the lookup searched on.
 *
 * Deliberately a CARD LIST, not an AG Grid: each loan expands into a detail
 * panel (term bar, field grid, match evidence, open action) — a record
 * drilldown, not a sortable table of rows.
 *
 * INFORMATION ONLY: nothing here blocks or raises a condition, so the only
 * warning colours are amber (30+ days delinquent) and red (foreclosure /
 * REO). SSN / DOB never reach this component — the backend doesn't return them.
 *
 * Used by the Applicants-tab card (per borrower, off the shared loan-level
 * fetch) and by the Add Applicant "Check prior loans" lookup. Every click
 * stops propagation: on the card, a bubbling click would open the borrower
 * sidebar.
 */
import {
  CheckRounded,
  ContentCopyRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
  InfoOutlined,
  OpenInNewRounded,
  PersonOutlineRounded,
  PriorityHighRounded,
  RefreshRounded,
} from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import PropTypes from "prop-types";
import { memo, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import Flx from "../../../components/layout/Flx";
import CollapsibleSectionHeader, {
  SummaryPill,
} from "../shared/CollapsibleSectionHeader";
import Txt from "../../../components/typography/Txt";
import { ink, slate, status } from "../../../theme/colors";
import {
  TONE,
  checkedAgo,
  checkedTooltip,
  fciStatusLabel,
  fmtLongDate,
  fmtMoney,
  foundIn,
  isBoarded,
  lateHistoryShort,
  lateHistoryText,
  loanHref,
  loanRef,
  loanTitle,
  matchedWithoutSsn,
  partialNotes,
  productLabel,
  splitLoans,
  termProgress,
  verdict,
} from "./applicantHistoryModel";

/** Loans shown before "Show all" — a heavy repeat borrower can have 30+. */
const INITIAL_LOANS = 5;

const TONE_FG = {
  [TONE.neutral]: slate[700],
  [TONE.good]: status.success.fg,
  [TONE.amber]: status.warning.fg,
  [TONE.red]: status.error.fg,
  [TONE.grey]: slate[500],
};

/** Pill / banner surfaces per tone. */
const TONE_SURFACE = {
  [TONE.neutral]: { bg: slate[50], border: slate[200], dot: slate[500] },
  [TONE.good]: {
    bg: status.success.bg,
    border: "#A7F3D0",
    dot: status.success.solid,
  },
  [TONE.amber]: {
    bg: status.warning.bg,
    border: "#FDE68A",
    dot: status.warning.solid,
  },
  [TONE.red]: { bg: status.error.bg, border: "#FECACA", dot: status.error.solid },
  [TONE.grey]: { bg: slate[100], border: slate[200], dot: slate[500] },
};

const stop = (e) => e.stopPropagation();

const LABEL_SX = { fontSize: 12, color: slate[500], lineHeight: 1.4 };
const VALUE_SX = { fontSize: 13.5, color: ink, lineHeight: 1.45 };

/* ── small pieces ──────────────────────────────────────── */

const LosBadge = ({ los }) =>
  los ? (
    <Box
      component="span"
      sx={{
        px: "5px",
        py: "1px",
        borderRadius: "4px",
        border: `1px solid ${slate[300]}`,
        background: "#fff",
        color: slate[600],
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: "0.04em",
        lineHeight: 1.4,
      }}
    >
      {los}
    </Box>
  ) : null;

const StatusPill = ({ fci }) => {
  const { label, short, tone } = fciStatusLabel(fci);
  const s = TONE_SURFACE[tone];
  return (
    <Tooltip title={fci?.fciStatus ? `${label} · FCI: ${fci.fciStatus}` : label}>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          px: "10px",
          py: "3px",
          borderRadius: "999px",
          border: `1px solid ${s.border}`,
          background: s.bg,
          color: TONE_FG[tone],
          fontSize: 12.5,
          fontWeight: 500,
          whiteSpace: "nowrap",
        }}
      >
        <Box
          component="span"
          sx={{ width: 6, height: 6, borderRadius: "50%", background: s.dot }}
        />
        {short}
      </Box>
    </Tooltip>
  );
};

const Callout = ({ children }) => (
  <Flx
    g={1}
    sx={{
      alignItems: "flex-start",
      px: 1.5,
      py: 1.25,
      borderRadius: "8px",
      background: status.info.bg,
      border: `1px solid ${slate[200]}`,
    }}
  >
    <InfoOutlined sx={{ fontSize: 16, color: status.info.fg, mt: "1px" }} />
    <Flx column g={0.25}>
      {children}
    </Flx>
  </Flx>
);

const Field = ({ label, children }) => (
  <Flx column sx={{ gap: "2px", minWidth: 0 }}>
    <Txt sx={LABEL_SX}>{label}</Txt>
    <Box sx={VALUE_SX}>{children}</Box>
  </Flx>
);

const CopyValue = ({ value }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return "—";
  return (
    <Flx ac sx={{ gap: "4px" }}>
      <span>{value}</span>
      <Tooltip title={copied ? "Copied" : "Copy"}>
        <IconButton
          size="small"
          onClick={(e) => {
            stop(e);
            navigator.clipboard?.writeText(String(value)).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
          sx={{ p: "2px", color: slate[400] }}
        >
          <ContentCopyRounded sx={{ fontSize: 13 }} />
        </IconButton>
      </Tooltip>
    </Flx>
  );
};

/* ── verdict banner ────────────────────────────────────── */

const VerdictBanner = ({ applicant, fciAvailable }) => {
  const v = verdict(applicant?.summary, { fciAvailable });
  const s = TONE_SURFACE[v.tone];
  const alertIcon = v.tone === TONE.amber || v.tone === TONE.red;
  // One compact row: a bare tone-coloured glyph (no tile), the title, then
  // the detail trailing on the same line.
  const iconSx = { fontSize: 16, flexShrink: 0, color: TONE_FG[v.tone] };
  return (
    <Flx
      ac
      g={1}
      sx={{
        px: 1.25,
        py: 0.75,
        borderRadius: "8px",
        background: s.bg,
        border: `1px solid ${s.border}`,
        minWidth: 0,
      }}
    >
      {v.tone === TONE.good ? (
        <CheckRounded sx={iconSx} />
      ) : alertIcon ? (
        <PriorityHighRounded sx={iconSx} />
      ) : (
        <InfoOutlined sx={{ ...iconSx, fontSize: 15, color: slate[500] }} />
      )}
      <Txt
        sx={{
          fontSize: 12.5,
          fontWeight: 600,
          color: ink,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {v.title}
      </Txt>
      {v.detail && (
        <Tooltip title={v.detail} enterDelay={600}>
          <Txt
            sx={{
              fontSize: 12,
              color: slate[600],
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {v.detail}
          </Txt>
        </Tooltip>
      )}
    </Flx>
  );
};

/* ── one prior loan ────────────────────────────────────── */

const TermBar = ({ row }) => {
  const t = termProgress(row?.fundedDate, row?.fci?.maturityDate);
  if (!t) return null;
  return (
    <Flx column sx={{ gap: "6px" }}>
      <Flx jb ac>
        <Txt sx={{ fontSize: 13, fontWeight: 600, color: ink }}>
          Month {t.month} of {t.totalMonths}
        </Txt>
        <Txt sx={{ fontSize: 12.5, color: slate[600] }}>
          {t.daysToMaturity >= 0
            ? `${t.daysToMaturity} days to maturity`
            : `Matured ${-t.daysToMaturity} days ago`}
        </Txt>
      </Flx>
      <Box
        sx={{
          position: "relative",
          height: 5,
          borderRadius: 3,
          background: slate[200],
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            width: `${t.pct * 100}%`,
            borderRadius: 3,
            background: "#0F6B6E",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: `${t.pct * 100}%`,
            width: 11,
            height: 11,
            transform: "translate(-50%, -50%)",
            borderRadius: "50%",
            background: "#fff",
            border: "2px solid #0F6B6E",
          }}
        />
      </Box>
      <Flx jb>
        <Txt sx={{ fontSize: 11.5, color: slate[500] }}>
          Funded {fmtLongDate(row.fundedDate)}
        </Txt>
        <Txt sx={{ fontSize: 11.5, color: slate[500] }}>
          Matures {fmtLongDate(row.fci.maturityDate)}
        </Txt>
      </Flx>
    </Flx>
  );
};

const MatchFooter = ({ row, currentLos }) => {
  // Only rows carrying a deal `_id` link. A Salesforce-only row (`sf:…`) has
  // no `_id`, and a loan URL must never be keyed by loanNumber (CLAUDE.md).
  const href = loanHref(row);
  const pct = Math.round((Number(row?.confidence) || 0) * 100);
  const strong = pct >= 95;
  const barColor = strong ? status.success.solid : status.warning.solid;
  const source = foundIn(row);
  const isSfOnly = String(row?.dealId || "").startsWith("sf:");
  return (
    <Flx
      ac
      jb
      g={1.5}
      wrap
      sx={{ pt: 1.5, borderTop: `1px solid ${slate[200]}` }}
    >
      <Flx ac g={1.25} wrap sx={{ fontSize: 12.5, color: slate[600] }}>
        <span>
          Matched on{" "}
          <Box component="b" sx={{ color: ink, fontWeight: 600 }}>
            {row?.basis || "—"}
          </Box>
        </span>
        {row?.confidence != null && (
          <Flx ac sx={{ gap: "8px" }}>
            <Box
              sx={{
                width: 64,
                height: 4,
                borderRadius: 2,
                background: slate[200],
                overflow: "hidden",
              }}
            >
              <Box
                sx={{ width: `${pct}%`, height: "100%", background: barColor }}
              />
            </Box>
            <span>{pct}%</span>
          </Flx>
        )}
        {source && <span>{source}</span>}
      </Flx>
      {href ? (
        <Button
          variant="contained"
          size="small"
          component={RouterLink}
          to={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={stop}
          endIcon={<OpenInNewRounded sx={{ fontSize: "15px !important" }} />}
          sx={{ fontSize: 12.5, fontWeight: 600, textTransform: "none" }}
        >
          Open {loanRef(row) || "loan"}
        </Button>
      ) : (
        <Button
          variant="outlined"
          color="gray"
          size="small"
          disabled
          sx={{ fontSize: 12.5, textTransform: "none" }}
        >
          {isSfOnly ? "Salesforce only" : `Can't open from ${currentLos || "EV"}`}
        </Button>
      )}
    </Flx>
  );
};

const LoanCard = ({ row, currentLos, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const fci = row?.fci ?? null;
  const boarded = isBoarded(fci);
  const ref = loanRef(row);
  const product = productLabel(row?.loanProductType);
  const balance = fci?.currentBalance;

  const meta = [
    ref && <span key="ref">{ref}</span>,
    row?.role && <span key="role">{row.role}</span>,
    product && <span key="prod">{product}</span>,
    <span key="funded">
      {row?.fundedDate ? `Funded ${fmtLongDate(row.fundedDate)}` : "Not funded"}
    </span>,
  ].filter(Boolean);

  return (
    <Box
      sx={{
        borderRadius: "10px",
        border: `1px solid ${slate[200]}`,
        background: "#fff",
        overflow: "hidden",
      }}
    >
      <ButtonBase
        onClick={(e) => {
          stop(e);
          setOpen((v) => !v);
        }}
        sx={{
          width: "100%",
          textAlign: "left",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          px: 2,
          py: 1.5,
          "&:hover": { background: slate[50] },
        }}
      >
        <Flx column sx={{ gap: "4px", minWidth: 0 }}>
          <Txt
            sx={{
              fontSize: 14.5,
              fontWeight: 600,
              color: ink,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {loanTitle(row)}
          </Txt>
          <Flx ac wrap sx={{ gap: "4px 12px", fontSize: 12.5, color: slate[500] }}>
            <LosBadge los={row?.originatingLos} />
            {meta}
            {matchedWithoutSsn(row) && (
              <Flx
                ac
                component="span"
                sx={{ gap: "3px", color: status.warning.fg }}
              >
                <PersonOutlineRounded sx={{ fontSize: 14 }} />
                Matched without SSN
              </Flx>
            )}
          </Flx>
        </Flx>
        <Flx ac g={2} sx={{ flexShrink: 0 }}>
          <StatusPill fci={fci} />
          <Txt
            sx={{
              minWidth: 72,
              textAlign: "right",
              fontSize: 13.5,
              fontWeight: balance != null ? 600 : 400,
              color: balance != null ? ink : slate[400],
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {balance != null ? fmtMoney(balance) : "No balance"}
          </Txt>
          {open ? (
            <ExpandLessRounded sx={{ fontSize: 20, color: slate[500] }} />
          ) : (
            <ExpandMoreRounded sx={{ fontSize: 20, color: slate[500] }} />
          )}
        </Flx>
      </ButtonBase>

      {open && (
        <Box
          onClick={stop}
          sx={{
            px: 2,
            pb: 2,
            pt: 0.5,
            background: slate[50],
            borderTop: `1px solid ${slate[100]}`,
          }}
        >
          <Flx
            column
            g={2}
            sx={{
              mt: 1,
              p: 2,
              borderRadius: "8px",
              border: `1px solid ${slate[200]}`,
              background: "#fff",
            }}
          >
            {fci?.note && (
              <Callout>
                <Txt sx={{ fontSize: 12.5, color: slate[700] }}>{fci.note}</Txt>
              </Callout>
            )}
            {!fci && (
              <Callout>
                <Txt sx={{ fontSize: 12.5, color: slate[700] }}>
                  FCI unavailable, servicing status not shown.
                </Txt>
              </Callout>
            )}

            {boarded && <TermBar row={row} />}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: "16px 24px",
              }}
            >
              {boarded && (
                <>
                  <Field label="FCI account">
                    <CopyValue value={fci.fciLoanAccount} />
                  </Field>
                  <Field label="Current balance">
                    {balance != null ? fmtMoney(balance) : "—"}
                  </Field>
                  <Field label="Days past due">
                    {fci.daysPastDue ?? "—"}
                    {fci.graceDays != null && (
                      <Box component="span" sx={{ color: slate[500] }}>
                        {" "}
                        ({fci.graceDays}-day grace)
                      </Box>
                    )}
                  </Field>
                  <Field label="Next payment">
                    {fmtLongDate(fci.nextDueDate)}
                  </Field>
                  <Field label="Late payments, 12 mo">
                    <Tooltip title={lateHistoryText(fci.late12Months) || ""}>
                      <span>{lateHistoryShort(fci.late12Months)}</span>
                    </Tooltip>
                  </Field>
                  <Field label="Default interest">
                    {fci.defaultInterestActive == null
                      ? "—"
                      : fci.defaultInterestActive
                        ? "Yes"
                        : "No"}
                  </Field>
                  <Field label="Boarded to FCI">
                    {fmtLongDate(fci.boardingDate)}
                  </Field>
                </>
              )}
              <Field label={`Status in ${row?.originatingLos || "LOS"}`}>
                {row?.loanStatus || "—"}
              </Field>
              <Field label="Product">{product || "—"}</Field>
            </Box>

            <MatchFooter row={row} currentLos={currentLos} />
          </Flx>
        </Box>
      )}
    </Box>
  );
};

const LoanList = ({ rows, currentLos, defaultOpen }) => {
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? rows : rows.slice(0, INITIAL_LOANS);
  return (
    <Flx column g={1}>
      {shown.map((row) => (
        <LoanCard
          key={`${row?.dealId || ""}:${row?.loanId || ""}:${row?.role || ""}`}
          row={row}
          currentLos={currentLos}
          defaultOpen={defaultOpen}
        />
      ))}
      {rows.length > INITIAL_LOANS && (
        <ButtonBase
          onClick={(e) => {
            stop(e);
            setShowAll((v) => !v);
          }}
          sx={{
            alignSelf: "flex-start",
            px: 1,
            py: 0.5,
            borderRadius: "6px",
            fontSize: 12.5,
            color: slate[600],
            "&:hover": { background: slate[100] },
          }}
        >
          {showAll ? "Show fewer" : `Show all ${rows.length} loans`}
        </ButtonBase>
      )}
    </Flx>
  );
};

/* ── section ───────────────────────────────────────────── */

const PriorLoansSection = ({
  applicant,
  payload,
  loading,
  refreshing,
  error,
  onRefresh,
  defaultExpanded = false,
  showHeader = true,
  missingText = "Not checked yet — refresh to look this applicant up.",
}) => {
  // The whole section folds from its header and starts folded (the
  // verdict pill still shows). Without a header (the Add
  // Applicant lookup) there is nothing to fold from, so it stays open.
  const [sectionOpen, setSectionOpen] = useState(!showHeader);
  const fciAvailable = payload?.searched?.fci !== false;
  const { matches, possible } = useMemo(
    () => splitLoans(applicant?.loans),
    [applicant],
  );
  const notes = useMemo(() => partialNotes(payload), [payload]);
  const ago = checkedAgo(payload?.generatedAt);
  const currentLos = payload?.currentLos;
  // One or two loans read best open; a long history starts collapsed.
  const openLoans = defaultExpanded || matches.length <= 2;

  const body = (() => {
    // Also while a re-check runs for an applicant the stored result predates.
    if ((loading || refreshing) && (!payload || !applicant)) {
      return (
        <Flx ac g={1} sx={{ py: 1 }}>
          <CircularProgress size={14} thickness={5} sx={{ color: slate[400] }} />
          <Txt sx={{ fontSize: 12.5, color: slate[500] }}>
            Checking prior loans and FCI…
          </Txt>
        </Flx>
      );
    }
    if (error && !payload) {
      return <Txt sx={{ fontSize: 12.5, color: slate[500] }}>{error}</Txt>;
    }
    if (!applicant) {
      return (
        <Txt sx={{ fontSize: 12.5, color: slate[500] }}>{missingText}</Txt>
      );
    }
    return (
      <Flx column g={1.25}>
        {/* Partial results sit ABOVE the verdict (applicant-history.md). */}
        {notes.length > 0 && (
          <Callout>
            {notes.map((note) => (
              <Txt key={note} sx={{ fontSize: 12.5, color: slate[700] }}>
                {note}
              </Txt>
            ))}
          </Callout>
        )}
        <VerdictBanner applicant={applicant} fciAvailable={fciAvailable} />
        {matches.length > 0 && (
          <LoanList rows={matches} currentLos={currentLos} defaultOpen={openLoans} />
        )}
        {possible.length > 0 && (
          <Flx column g={0.75} sx={{ opacity: 0.65 }}>
            <Txt sx={{ fontSize: 12, fontWeight: 600, color: slate[500] }}>
              Possible matches (not confirmed)
            </Txt>
            <LoanList rows={possible} currentLos={currentLos} defaultOpen={false} />
          </Flx>
        )}
        {applicant?.loansTruncated && (
          <Txt sx={{ fontSize: 11.5, color: slate[500] }}>
            Showing the first 200 loans.
          </Txt>
        )}
        {applicant?.searchedOn?.length > 0 && (
          <Flx ac wrap sx={{ gap: "6px", mt: 0.25 }}>
            <Txt sx={{ fontSize: 12, color: slate[500], mr: "2px" }}>
              Searched by
            </Txt>
            {applicant.searchedOn.map((id) => (
              <Box
                key={id}
                component="span"
                sx={{
                  px: "8px",
                  py: "2px",
                  borderRadius: "6px",
                  border: `1px solid ${slate[200]}`,
                  background: slate[50],
                  fontSize: 12,
                  color: slate[600],
                }}
              >
                {id}
              </Box>
            ))}
          </Flx>
        )}
      </Flx>
    );
  })();

  // Collapsed, the header still carries the verdict so nothing is hidden.
  const collapsedVerdict =
    !sectionOpen && applicant
      ? verdict(applicant.summary, { fciAvailable })
      : null;

  return (
    <Box onClick={stop} onMouseDown={stop} sx={{ cursor: "default" }}>
      {(showHeader || onRefresh) && (
        <Flx ac jb g={0.5} sx={{ mb: sectionOpen ? 1.25 : 0 }}>
          {showHeader ? (
            <CollapsibleSectionHeader
              title="Prior loans & servicing"
              open={sectionOpen}
              onToggle={() => setSectionOpen((v) => !v)}
              summary={
                collapsedVerdict && (
                  <SummaryPill
                    bg={TONE_SURFACE[collapsedVerdict.tone].bg}
                    border={TONE_SURFACE[collapsedVerdict.tone].border}
                    color={TONE_FG[collapsedVerdict.tone]}
                    dot={TONE_SURFACE[collapsedVerdict.tone].dot}
                  >
                    {collapsedVerdict.title}
                  </SummaryPill>
                )
              }
              meta={
                // "Checked …" rides inside the header (right-aligned) so the
                // row is one click target; only the refresh button sits out.
                ago && (
                  <Tooltip title={checkedTooltip(payload?.generatedAt)}>
                    <Txt
                      sx={{
                        ml: "auto",
                        pl: 1.5,
                        fontSize: 11.5,
                        color: slate[400],
                        whiteSpace: "nowrap",
                      }}
                    >
                      Checked {ago}
                    </Txt>
                  </Tooltip>
                )
              }
            />
          ) : (
            <span />
          )}
          {onRefresh && (
            <Flx ac sx={{ gap: "4px" }}>
              {ago && !showHeader && (
                <Tooltip title={checkedTooltip(payload?.generatedAt)}>
                  <Txt sx={{ fontSize: 11.5, color: slate[400] }}>
                    Checked {ago}
                  </Txt>
                </Tooltip>
              )}
              <Tooltip title="Re-check prior loans and FCI">
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      stop(e);
                      onRefresh();
                    }}
                    disabled={loading || refreshing}
                    sx={{ p: "4px", color: slate[500] }}
                  >
                    {refreshing ? (
                      <CircularProgress
                        size={13}
                        thickness={5}
                        sx={{ color: slate[400] }}
                      />
                    ) : (
                      <RefreshRounded sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Flx>
          )}
        </Flx>
      )}
      {sectionOpen && body}
    </Box>
  );
};

PriorLoansSection.propTypes = {
  applicant: PropTypes.object,
  payload: PropTypes.object,
  loading: PropTypes.bool,
  refreshing: PropTypes.bool,
  error: PropTypes.string,
  onRefresh: PropTypes.func,
  defaultExpanded: PropTypes.bool,
  showHeader: PropTypes.bool,
  missingText: PropTypes.string,
};

export default memo(PriorLoansSection);
